import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { forumApi } from '../api/forum';
import { filesApi } from '../api/files';
import { UserContext } from '../context/UserContext';
import { isAdmin, isModerator } from '../admin/utils/roles';
import HrnbCard from '../components/controls/HrnbCard';
import HrnbButton from '../components/controls/HrnbButton';
import HrnbLoader from '../components/controls/HrnbLoader';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbTextArea from '../components/controls/HrnbTextArea';
import HrnbSelect from '../components/controls/HrnbSelect';
import HrnbModal from '../components/controls/HrnbModal';
import HrnbErrorBoundary from '../components/controls/HrnbErrorBoundary';
import { BackIcon, DeleteIcon, SendIcon, ClearIcon, ForwardIcon, EditIcon, SaveIcon } from '../icons';
import FileUploader from '../components/forum/FileUploader';
import AttachmentList from '../components/forum/AttachmentList';
import MarkdownToolbar from '../components/forum/MarkdownToolbar';
import ShareToChatModal from '../components/forum/ShareToChatModal';
import { renderMarkdownLite } from '../utils/markdownLite';
import { formatRelativeTime } from '../utils/relativeTime';
import '../css/pages/PostDetailPage.css';

const COMMENT_PAGE_SIZE = 50; // Загружаем больше комментариев за раз, чтобы дерево строилось корректно

export default function PostDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useContext(UserContext);

    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [newComment, setNewComment] = useState('');
    const [commentLoading, setCommentLoading] = useState(false);
    const [liked, setLiked] = useState(false);
    const [likes, setLikes] = useState(0);
    const [authorProfileId, setAuthorProfileId] = useState(null);
    const [commentFiles, setCommentFiles] = useState([]);

    // Состояние для ответа на комментарий
    const [replyTo, setReplyTo] = useState(null); // { comment, parentId? }
    const [replyContent, setReplyContent] = useState('');
    const [replyFiles, setReplyFiles] = useState([]);
    const [expandedReplies, setExpandedReplies] = useState(new Set()); // Set<commentId>

    // Состояние для редактирования комментария
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingContent, setEditingContent] = useState('');
    const [editingLoading, setEditingLoading] = useState(false);

    const commentTextareaRef = useRef(null);
    const replyTextareaRef = useRef(null);

    // Состояние для удаления поста
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Состояние для «Поделиться в чат»
    const [shareModalOpen, setShareModalOpen] = useState(false);

    // Состояние для удаления комментария
    const [deletingCommentId, setDeletingCommentId] = useState(null);

    // Состояние для загрузки файлов к посту (при редактировании)
    const [postFiles, setPostFiles] = useState([]);

    // Пагинация комментариев
    const [commentPage, setCommentPage] = useState(0);
    const [commentHasMore, setCommentHasMore] = useState(true);
    const [commentTotal, setCommentTotal] = useState(0);
    const [commentLoadingMore, setCommentLoadingMore] = useState(false);

    // Скролл к комментарию по хешу в URL
    const [hashVersion, setHashVersion] = useState(0);

    useEffect(() => {
        const onHashChange = () => setHashVersion(v => v + 1);
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    // Сброс всех состояний поста при переходе между постами
    useEffect(() => {
        setPost(null);
        setComments([]);
        setError(null);
        setLiked(false);
        setLikes(0);
        setNewComment('');
        setCommentFiles([]);
        setReplyTo(null);
        setReplyContent('');
        setReplyFiles([]);
        setExpandedReplies(new Set());
        setEditingCommentId(null);
        setEditingContent('');
        setPostFiles([]);
        setDeleteModalOpen(false);
        setDeleting(false);
        setDeletingCommentId(null);
        setCommentPage(0);
        setCommentHasMore(true);
        setCommentTotal(0);
        setCommentLoadingMore(false);
    }, [id]);

    useEffect(() => {
        if (!currentUser) return;
        const pid = currentUser.profiles?.[0]?.id;
        if (pid) {
            setAuthorProfileId(pid);
        }
    }, [currentUser]);

    const fetchPost = useCallback(async () => {
        try {
            setLoading(true);
            const data = await forumApi.getPost(id);
            setPost(data);
            setLiked(data.liked || false);
            setLikes(data.likeCount ?? data.likes ?? 0);
            setError(null);
        } catch (err) {
            console.error('[PostDetailPage] Failed to fetch post:', err.message);
            setError('Не удалось загрузить пост');
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchComments = useCallback(async (page = 0, append = false) => {
        try {
            if (append) {
                setCommentLoadingMore(true);
            }
            const data = await forumApi.getPostComments(id, { page, size: COMMENT_PAGE_SIZE });

            // API может возвращать { content: [...], totalElements: N } или просто массив
            const items = data.content || data || [];
            const total = data.totalElements ?? data.total ?? items.length;

            if (append) {
                setComments(prev => {
                    // Дедупликация: не добавляем комментарии, которые уже есть
                    const existingIds = new Set(prev.map(c => c.id));
                    const newItems = items.filter(c => !existingIds.has(c.id));
                    return [...prev, ...newItems];
                });
            } else {
                setComments(items);
            }

            setCommentTotal(total);
            setCommentPage(page + 1);
            // Есть ли ещё страницы — если вернули полный размер страницы и всего больше загруженного
            const loadedCount = append ? (page + 1) * COMMENT_PAGE_SIZE : items.length;
            setCommentHasMore(items.length >= COMMENT_PAGE_SIZE && loadedCount < total);
        } catch (err) {
            console.error('[PostDetailPage] Failed to fetch comments:', err.message);
        } finally {
            if (append) {
                setCommentLoadingMore(false);
            }
        }
    }, [id]);

    useEffect(() => {
        fetchPost();
        fetchComments(0, false);
    }, [id, fetchPost, fetchComments]);

    // Авто-раскрытие: все комментарии, у которых есть ответы, развёрнуты по умолчанию
    useEffect(() => {
        if (comments.length === 0) return;
        const parentIds = new Set();
        comments.forEach(c => {
            const pid = c.parentComment?.id || c.parentCommentId;
            if (pid) {
                parentIds.add(pid);
            }
        });
        if (parentIds.size > 0) {
            setExpandedReplies(prev => {
                const next = new Set(prev);
                parentIds.forEach(id => next.add(id));
                return next;
            });
        }
    }, [comments]);

    // Скролл к комментарию по URL-хешу (#comment-N)
    useEffect(() => {
        const hash = window.location.hash;
        if (!hash || !hash.startsWith('#comment-')) return;
        if (comments.length === 0) return;

        const commentId = hash.replace('#comment-', '');
        // Небольшая задержка — ждём отрисовки DOM
        const timer = setTimeout(() => {
            const el = document.getElementById(`comment-${commentId}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('comment-card-highlighted');
                setTimeout(() => el.classList.remove('comment-card-highlighted'), 3000);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [comments.length, hashVersion, location.hash]);

    const handleAddComment = async (parentCommentId = null, replyContentText = null, replyFilesList = null) => {
        const isReply = parentCommentId !== null;
        const content = isReply ? (replyContentText || replyContent) : newComment;
        const files = isReply ? (replyFilesList || replyFiles) : commentFiles;

        if (!content.trim() && files.length === 0) {
            setError(isReply ? 'Введите текст ответа или прикрепите файл' : 'Введите текст комментария или прикрепите файл');
            return;
        }
        if (!authorProfileId) {
            setError('Не удалось определить профиль автора. Попробуйте позже.');
            return;
        }

        try {
            setCommentLoading(true);
            const attachmentIds = files.map(f => f.id);

            await forumApi.addCommentToPost(id, {
                content: content.trim(),
                postId: id,
                authorId: authorProfileId,
                parentCommentId: parentCommentId || undefined,
                attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
            });

            if (isReply) {
                setReplyContent('');
                setReplyFiles([]);
                setReplyTo(null);
                // Автоматически раскрываем ответы после отправки
                if (parentCommentId) {
                    setExpandedReplies(prev => new Set(prev).add(parentCommentId));
                }
            } else {
                setNewComment('');
                setCommentFiles([]);
            }

            await fetchComments(0, false);
        } catch (err) {
            console.error('[PostDetailPage] Failed to add comment:', err.message);
            setError('Не удалось добавить комментарий');
        } finally {
            setCommentLoading(false);
        }
    };

    const handleReply = (comment) => {
        setReplyTo({ comment });
        setReplyContent('');
        setReplyFiles([]);
        setError(null);
        // Фокус на поле ответа после рендера
        setTimeout(() => {
            replyTextareaRef.current?.focus();
        }, 100);
    };

    const cancelReply = () => {
        setReplyTo(null);
        setReplyContent('');
        setReplyFiles([]);
    };

    // === Редактирование комментария ===
    const handleEditComment = (comment) => {
        setEditingCommentId(comment.id);
        setEditingContent(comment.content || '');
        setError(null);
        setTimeout(() => {
            replyTextareaRef.current?.focus();
        }, 100);
    };

    const cancelEdit = () => {
        setEditingCommentId(null);
        setEditingContent('');
    };

    const handleSaveEdit = async (commentId) => {
        if (!editingContent.trim()) return;
        setEditingLoading(true);
        setError(null);
        try {
            await forumApi.updateComment(commentId, { content: editingContent });
            setComments(prev => prev.map(c =>
                c.id === commentId ? { ...c, content: editingContent, updatedAt: new Date().toISOString() } : c
            ));
            setEditingCommentId(null);
            setEditingContent('');
        } catch (err) {
            console.error('[PostDetailPage] Failed to edit comment:', err.message);
            setError('Не удалось отредактировать комментарий');
        } finally {
            setEditingLoading(false);
        }
    };

    // === Цитирование комментария ===
    const handleQuoteComment = (comment) => {
        const author = comment.author?.displayName || comment.author?.username || 'пользователь';
        const quotedText = comment.content || '';
        const quoteBlock = `[quote]${author}:\n${quotedText}[/quote]\n\n`;

        if (replyTo?.comment?.id === comment.id) {
            // Если форма ответа уже открыта — добавляем цитату в начало
            setReplyContent(prev => quoteBlock + prev);
        } else {
            setReplyTo({ comment });
            setReplyContent(quoteBlock);
            setReplyFiles([]);
        }
        setError(null);
        setTimeout(() => {
            replyTextareaRef.current?.focus();
            // Ставим курсор в конец
            const el = replyTextareaRef.current;
            if (el) {
                el.setSelectionRange(el.value.length, el.value.length);
            }
        }, 100);
    };

    const handleReplyFileUploaded = (fileData) => {
        setReplyFiles(prev => [...prev, fileData]);
    };

    const handleReplyFileRemoved = (fileId) => {
        setReplyFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const toggleReplies = (commentId) => {
        setExpandedReplies(prev => {
            const next = new Set(prev);
            if (next.has(commentId)) {
                next.delete(commentId);
            } else {
                next.add(commentId);
            }
            return next;
        });
    };

    // Строим дерево комментариев: { rootComments: [...], childrenMap: { parentId: [child, ...] } }
    const buildCommentTree = (flatComments) => {
        const childrenMap = {};
        const rootComments = [];

        flatComments.forEach(c => {
            // API возвращает parentComment (объект), а не parentCommentId
            const parentId = c.parentComment?.id || c.parentCommentId;
            if (parentId) {
                if (!childrenMap[parentId]) childrenMap[parentId] = [];
                childrenMap[parentId].push(c);
            } else {
                rootComments.push(c);
            }
        });

        return { rootComments, childrenMap };
    };

    const getRepliesCount = (commentId, flatComments) => {
        return flatComments.filter(c => (c.parentComment?.id || c.parentCommentId) === commentId).length;
    };

    // Рекурсивный рендер комментария с ответами
    const renderComment = (comment, depth = 0, childrenMap) => {
        const children = childrenMap[comment.id] || [];
        const hasChildren = children.length > 0;
        const isExpanded = expandedReplies.has(comment.id);
        const replyCount = getRepliesCount(comment.id, comments);

        // Поиск родительского комментария для отображения ссылки «в ответ на»
        // API возвращает parentComment (объект) прямо в ответе
        const parentComment = comment.parentComment || null;
        const parentAuthor = parentComment?.author
            ? (parentComment.author.displayName || parentComment.author.username || parentComment.author.name || 'пользователь')
            : null;

        return (
            <div key={comment.id} className="comment-tree-node">
                {depth > 0 && <div className="comment-tree-connector" />}
                <HrnbCard
                    id={`comment-${comment.id}`}
                    className={`comment-card ${hasChildren ? 'comment-card-has-replies' : ''} ${comment.status === 'HIDDEN' ? 'comment-card-hidden' : ''} ${comment.status === 'DELETED' ? 'comment-card-deleted' : ''}`}
                >
                    <div className="comment-card-header">
                        <span className="comment-card-author">
                            {comment.author?.id
                                ? <Link to={`/profile/${comment.author.id}`} className="comment-author-link">{comment.author?.displayName || comment.author?.username || comment.author?.name || 'Аноним'}</Link>
                                : (comment.author?.displayName || comment.author?.username || comment.author?.name || 'Аноним')}
                        </span>
                        {parentAuthor && (
                            <span className="comment-card-parent-ref">
                                <span className="comment-card-parent-arrow">↳</span>
                                <span className="comment-card-parent-text">
                                    в ответ{' '}
                                    {parentComment?.author?.id
                                        ? <Link to={`/profile/${parentComment.author.id}`} className="comment-card-parent-link" onClick={(e) => e.stopPropagation()}>{parentAuthor}</Link>
                                        : <span className="comment-card-parent-name">{parentAuthor}</span>}
                                </span>
                            </span>
                        )}
                        <span className="comment-card-date" title={formatFullDate(comment.createdAt)}>
                            {formatRelativeTime(comment.createdAt, { fallbackFull: true })}
                        </span>
                        {comment.status && comment.status !== 'PUBLISHED' && (
                            <span className={`comment-card-status comment-status-${comment.status.toLowerCase()}`}>
                                {comment.status === 'HIDDEN' && 'Скрыт'}
                                {comment.status === 'DELETED' && 'Удалён'}
                            </span>
                        )}
                    </div>
                    <div className="comment-card-content">
                        {comment.status === 'DELETED' ? 'Комментарий удалён' : (
                            editingCommentId === comment.id ? (
                                <div className="comment-edit-form">
                                    <MarkdownToolbar
                                        textareaRef={replyTextareaRef}
                                        value={editingContent}
                                        onChange={setEditingContent}
                                        disabled={editingLoading}
                                    />
                                    <HrnbTextArea
                                        ref={replyTextareaRef}
                                        value={editingContent}
                                        onChange={(e) => setEditingContent(e.target.value)}
                                        rows={3}
                                        disabled={editingLoading}
                                    />
                                    <div className="comment-edit-form-actions">
                                        <HrnbButton variant="outline" onClick={cancelEdit} disabled={editingLoading} size="sm">
                                            Отмена
                                        </HrnbButton>
                                        <HrnbButton
                                            variant="primary"
                                            onClick={() => handleSaveEdit(comment.id)}
                                            disabled={editingLoading || !editingContent.trim()}
                                            size="sm"
                                            icon={<SaveIcon />}
                                        >
                                            {editingLoading ? <HrnbLoader size="sm" /> : 'Сохранить'}
                                        </HrnbButton>
                                    </div>
                                </div>
                            ) : renderMarkdownLite(comment.content)
                        )}
                    </div>
                    {comment.updatedAt && comment.updatedAt !== comment.createdAt && editingCommentId !== comment.id && (
                        <div className="comment-card-edited">изменено</div>
                    )}
                    {comment.attachments && comment.attachments.length > 0 && (
                        <AttachmentList attachments={comment.attachments} />
                    )}
                    <div className="comment-card-actions">
                        <span
                            className={`comment-card-like ${currentUser ? 'comment-card-like-clickable' : ''}`}
                            onClick={currentUser ? () => handleCommentLike(comment) : undefined}
                            title={currentUser ? 'Нажмите для лайка' : 'Войдите, чтобы поставить лайк'}
                        >
                            {comment.liked ? '❤️' : '🤍'} {comment.likeCount ?? comment.likes ?? 0}
                        </span>
                        <div className="comment-card-actions-right">
                            {currentUser && comment.status !== 'DELETED' && (
                                <HrnbButton
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleReply(comment)}
                                    title="Ответить на комментарий"
                                    icon={<ForwardIcon />}
                                >
                                    Ответить
                                </HrnbButton>
                            )}
                            {currentUser && comment.status !== 'DELETED' && (
                                <HrnbButton
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleQuoteComment(comment)}
                                    title="Процитировать комментарий"
                                >
                                    Цитировать
                                </HrnbButton>
                            )}
                            {(comment.author?.id === authorProfileId || comment.author?.userId === currentUser?.id) && comment.status !== 'DELETED' && editingCommentId !== comment.id && (
                                <HrnbButton
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEditComment(comment)}
                                    title="Редактировать комментарий"
                                    icon={<EditIcon />}
                                >
                                    Редактировать
                                </HrnbButton>
                            )}
                            {(comment.author?.id === authorProfileId || comment.author?.userId === currentUser?.id || isAdmin(currentUser) || isModerator(currentUser)) && comment.status !== 'DELETED' && (
                                <HrnbButton
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDeleteComment(comment.id)}
                                    disabled={deletingCommentId === comment.id}
                                    className="comment-delete-btn"
                                    icon={deletingCommentId === comment.id ? null : <DeleteIcon />}
                                >
                                    {deletingCommentId === comment.id ? <HrnbLoader size="sm" /> : 'Удалить'}
                                </HrnbButton>
                            )}
                            {isModerator(currentUser) && comment.status !== 'DELETED' && (
                                <div className="comment-moderation">
                                    <HrnbSelect
                                        value={comment.status || 'PUBLISHED'}
                                        onChange={(value) => handleCommentStatusChange(comment.id, value)}
                                        options={[
                                            { value: 'PUBLISHED', label: 'Опубликован' },
                                            { value: 'HIDDEN', label: 'Скрыт' },
                                            { value: 'DELETED', label: 'Удалён' },
                                        ]}
                                        size="sm"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </HrnbCard>

                {/* Форма ответа под этим комментарием */}
                {replyTo?.comment?.id === comment.id && (
                    <div className="comment-reply-form">
                        <div className="comment-reply-notice">
                            <span>Ответ {comment.author?.displayName || comment.author?.username || 'пользователю'}</span>
                            <HrnbButton
                                variant="outline"
                                size="sm"
                                onClick={cancelReply}
                                title="Отменить ответ"
                                icon={<ClearIcon />}
                            />
                        </div>
                        <MarkdownToolbar
                            textareaRef={replyTextareaRef}
                            value={replyContent}
                            onChange={setReplyContent}
                            disabled={commentLoading}
                        />
                        <HrnbTextArea
                            ref={replyTextareaRef}
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            placeholder="Ваш ответ..."
                            rows={3}
                            disabled={commentLoading}
                        />
                        <FileUploader
                            onFileUploaded={handleReplyFileUploaded}
                            onFileRemoved={handleReplyFileRemoved}
                            uploadedFiles={replyFiles}
                            metadata="forum_attachment"
                            disabled={commentLoading}
                        />
                        <div className="comment-reply-form-actions">
                            <HrnbButton variant="outline" onClick={cancelReply} disabled={commentLoading} size="sm">
                                Отмена
                            </HrnbButton>
                            <HrnbButton
                                variant="primary"
                                onClick={() => handleAddComment(comment.id, replyContent, replyFiles)}
                                disabled={commentLoading || (!replyContent.trim() && replyFiles.length === 0)}
                                size="sm"
                                icon={<SendIcon />}
                            >
                                {commentLoading ? <HrnbLoader size="sm" /> : 'Ответить'}
                            </HrnbButton>
                        </div>
                    </div>
                )}

                {/* Дочерние комментарии (вложенное дерево) */}
                {hasChildren && (
                    <div className="comment-replies-section">
                        {isExpanded ? (
                            <div className="comment-tree-children">
                                {children.map(child => renderComment(child, depth + 1, childrenMap))}
                            </div>
                        ) : (
                            <HrnbButton
                                variant="outline"
                                size="sm"
                                onClick={() => toggleReplies(comment.id)}
                                className="comment-replies-toggle"
                            >
                                Показать ответы ({replyCount})
                            </HrnbButton>
                        )}
                        {isExpanded && (
                            <HrnbButton
                                variant="outline"
                                size="sm"
                                onClick={() => toggleReplies(comment.id)}
                                className="comment-replies-toggle"
                            >
                                Свернуть
                            </HrnbButton>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const handleLike = async () => {
        if (!currentUser) {
            setError('Необходимо авторизоваться для лайка');
            return;
        }
        try {
            if (liked) {
                await forumApi.unlikePost(id);
                setLikes(prev => prev - 1);
            } else {
                await forumApi.likePost(id);
                setLikes(prev => prev + 1);
            }
            setLiked(!liked);
        } catch (err) {
            console.error('[PostDetailPage] Like failed:', err.message);
            setError('Не удалось поставить лайк');
        }
    };

    const handleCommentLike = async (comment) => {
        if (!currentUser) {
            setError('Необходимо авторизоваться для лайка');
            return;
        }
        try {
            if (comment.liked) {
                await forumApi.unlikeComment(comment.id);
            } else {
                await forumApi.likeComment(comment.id);
            }
            await fetchComments(0, false);
        } catch (err) {
            console.error('[PostDetailPage] Comment like failed:', err.message);
            setError('Не удалось поставить лайк комментарию');
        }
    };

    const handleCommentStatusChange = async (commentId, status) => {
        try {
            await forumApi.updateCommentStatus(commentId, status);
            await fetchComments(0, false);
        } catch (err) {
            console.error('[PostDetailPage] Failed to update comment status:', err.message);
            setError('Не удалось изменить статус комментария');
        }
    };

    const handleCommentFileUploaded = (fileData) => {
        setCommentFiles(prev => [...prev, fileData]);
    };

    const handleCommentFileRemoved = (fileId) => {
        setCommentFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const handleDeletePost = async () => {
        if (!currentUser) {
            setError('Необходимо авторизоваться');
            return;
        }

        const isAuthor = post.author?.id === authorProfileId || post.author?.userId === currentUser?.id;
        const canDelete = isAuthor || isAdmin(currentUser) || isModerator(currentUser);

        if (!canDelete) {
            setError('У вас нет прав для удаления этого поста');
            return;
        }

        try {
            setDeleting(true);
            await forumApi.deletePost(id);
            setDeleteModalOpen(false);
            navigate('/forum');
        } catch (err) {
            console.error('[PostDetailPage] Failed to delete post:', err.message);
            setError(err.response?.data?.message || 'Не удалось удалить пост');
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!currentUser) {
            setError('Необходимо авторизоваться');
            return;
        }

        const comment = comments.find(c => c.id === commentId);
        const isAuthor = comment?.author?.id === authorProfileId || comment?.author?.userId === currentUser.id;
        const canDelete = isAuthor || isAdmin(currentUser) || isModerator(currentUser);

        if (!canDelete) {
            setError('У вас нет прав для удаления этого комментария');
            return;
        }

        try {
            setDeletingCommentId(commentId);
            await forumApi.deleteComment(commentId);
            await fetchComments(0, false);
        } catch (err) {
            console.error('[PostDetailPage] Failed to delete comment:', err.message);
            setError(err.response?.data?.message || 'Не удалось удалить комментарий');
        } finally {
            setDeletingCommentId(null);
        }
    };

    const handlePostFileUploaded = (fileData) => {
        setPostFiles(prev => [...prev, fileData]);
    };

    const handlePostFileRemoved = (fileId) => {
        setPostFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const formatFullDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (loading) {
        return (
            <div className="post-detail-page">
                <HrnbLoader />
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="post-detail-page">
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error || 'Пост не найден'}
                </HrnbAlert>
                <HrnbButton onClick={() => navigate('/forum')} className="mt-3" icon={<BackIcon />}>
                    Вернуться к списку
                </HrnbButton>
            </div>
        );
    }

    const {
        title,
        content,
        author,
        category,
        tags = [],
        createdAt,
        commentsCount = 0,
        commentCount,
        viewCount = 0,
    } = post;

    const displayCommentsCount = commentCount ?? commentsCount;

    return (
        <div className="post-detail-page">
            <HrnbButton variant="outline" onClick={() => navigate('/forum')} className="mb-3" icon={<BackIcon />}>
                Назад к списку
            </HrnbButton>

            <HrnbErrorBoundary title="Ошибка загрузки поста" message="Не удалось отобразить содержимое поста.">
            <HrnbCard className="post-detail-card">
                <div className="post-detail-header">
                    <div className="post-detail-header-actions">
                        <h1 className="post-detail-title">{title}</h1>
                        <div className="post-detail-actions">
                            {currentUser && (
                                <HrnbButton
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShareModalOpen(true)}
                                    title="Поделиться в чат"
                                    icon={<ForwardIcon />}
                                >
                                    Поделиться
                                </HrnbButton>
                            )}
                            {(post.author?.id === authorProfileId || post.author?.userId === currentUser?.id || isAdmin(currentUser) || isModerator(currentUser)) && (
                                <HrnbButton
                                    variant="danger"
                                    size="sm"
                                    onClick={() => setDeleteModalOpen(true)}
                                    className="post-detail-delete-btn"
                                    icon={<DeleteIcon />}
                                >
                                    Удалить пост
                                </HrnbButton>
                            )}
                        </div>
                    </div>
                {category && (
                    <span className="post-detail-category">
                        {typeof category === 'string' ? category : (category.name || 'Категория')}
                    </span>
                )}
                </div>

                <div className="post-detail-meta">
                    {author && (
                        <span className="post-detail-author">
                            Автор:{' '}
                            {typeof author === 'string'
                                ? author
                                : (author.id
                                    ? <Link to={`/profile/${author.id}`} className="post-detail-author-link">{author.displayName || author.username || author.name || 'Аноним'}</Link>
                                    : (author.displayName || author.username || author.name || 'Аноним'))}
                        </span>
                    )}
                    {createdAt && (
                        <span className="post-detail-date" title={formatFullDate(createdAt)}>
                            Опубликовано: {formatRelativeTime(createdAt, { fallbackFull: true })}
                        </span>
                    )}
                </div>

                <div className="post-detail-content">
                    {renderMarkdownLite(content)}
                </div>

                {post.attachments && post.attachments.length > 0 && (
                    <AttachmentList attachments={post.attachments} />
                )}

                {tags && tags.length > 0 && (
                    <div className="post-detail-tags">
                        {tags.map((tag, index) => (
                            <span key={tag.id || index} className="post-detail-tag">
                                #{typeof tag === 'string' ? tag : (tag.name || 'тег')}
                            </span>
                        ))}
                    </div>
                )}

                <div className="post-detail-stats">
                    <span className="post-detail-stat">
                        👁 {viewCount} просмотров
                    </span>
                    <span
                        className={`post-detail-stat post-detail-like-stat ${currentUser ? 'post-detail-like-clickable' : ''} ${liked ? 'post-detail-liked' : ''}`}
                        onClick={currentUser ? handleLike : undefined}
                        title={currentUser ? 'Нажмите для лайка' : 'Войдите, чтобы поставить лайк'}
                    >
                        👍 {likes} {likes === 1 ? 'лайк' : likes < 5 ? 'лайка' : 'лайков'}
                    </span>
                    <span className="post-detail-stat">
                        💬 {displayCommentsCount} комментариев
                    </span>
                </div>
            </HrnbCard>
            </HrnbErrorBoundary>

            <HrnbErrorBoundary title="Ошибка загрузки комментариев" message="Не удалось отобразить комментарии.">
            <div className="post-detail-comments">
                <h2 className="post-detail-comments-title">
                    Комментарии ({commentTotal || comments.length})
                </h2>

                {currentUser ? (
                    <div className="post-detail-comment-form">
                        <MarkdownToolbar
                            textareaRef={commentTextareaRef}
                            value={newComment}
                            onChange={setNewComment}
                            disabled={commentLoading}
                        />
                        <HrnbTextArea
                            ref={commentTextareaRef}
                            label="Добавить комментарий"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Напишите ваш комментарий..."
                            rows={4}
                            disabled={commentLoading}
                        />
                        <FileUploader
                            onFileUploaded={handleCommentFileUploaded}
                            onFileRemoved={handleCommentFileRemoved}
                            uploadedFiles={commentFiles}
                            metadata="forum_attachment"
                            disabled={commentLoading}
                        />
                        <HrnbButton
                            variant="primary"
                            onClick={() => handleAddComment()}
                            disabled={commentLoading || (!newComment.trim() && commentFiles.length === 0)}
                            icon={<SendIcon />}
                        >
                            {commentLoading ? <HrnbLoader size="sm" /> : 'Отправить'}
                        </HrnbButton>
                    </div>
                ) : (
                    <div className="post-detail-login-prompt">
                        <p>Войдите, чтобы оставить комментарий</p>
                    </div>
                )}

                <div className="post-detail-comments-list">
                    {comments.length > 0 ? (
                        (() => {
                            const { rootComments, childrenMap } = buildCommentTree(comments);
                            return rootComments.map(comment => renderComment(comment, 0, childrenMap));
                        })()
                    ) : (
                        <p className="no-comments">Комментариев пока нет</p>
                    )}
                </div>

                {commentHasMore && (
                    <div className="post-detail-comments-load-more">
                        <HrnbButton
                            variant="outline"
                            onClick={() => fetchComments(commentPage, true)}
                            disabled={commentLoadingMore}
                        >
                            {commentLoadingMore ? <HrnbLoader size="sm" /> : 'Показать ещё'}
                        </HrnbButton>
                    </div>
                )}
            </div>
            </HrnbErrorBoundary>

            {/* Модальное окно подтверждения удаления поста */}
            <HrnbModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Удаление поста"
                size="small"
            >
                <div className="delete-modal-content">
                    <p className="delete-modal-text">
                        Вы уверены, что хотите удалить этот пост? Это действие нельзя отменить.
                    </p>
                    <div className="delete-modal-actions">
                        <HrnbButton
                            variant="outline"
                            onClick={() => setDeleteModalOpen(false)}
                            disabled={deleting}
                            icon={<ClearIcon />}
                        >
                            Отмена
                        </HrnbButton>
                        <HrnbButton
                            variant="danger"
                            onClick={handleDeletePost}
                            disabled={deleting}
                            icon={<DeleteIcon />}
                        >
                            {deleting ? <HrnbLoader size="sm" /> : 'Удалить'}
                        </HrnbButton>
                    </div>
                </div>
            </HrnbModal>

            <ShareToChatModal
                isOpen={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                postUrl={`${window.location.origin}/forum/posts/${id}`}
                postTitle={title}
            />
        </div>
    );
}
